import React, { useState } from "react";
import { Search, ChevronDown, Menu, X, LogIn, UserCircle2, Bot } from "lucide-react";
import { Link } from "react-router-dom";
import { navigation } from "../mock";
import { useSearch } from "../context/SearchContext";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";
import ProfileSheet from "./ProfileSheet";

export default function Header() {
  const [openMobile, setOpenMobile] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { submit } = useSearch();
  const [local, setLocal] = useState("");
  const { user, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [profileOpen, setProfileOpen] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    submit(local);
  };

  const openLogin = () => { setAuthTab("login"); setAuthOpen(true); };
  const openRegister = () => { setAuthTab("register"); setAuthOpen(true); };

  const initials = (user?.name || "").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "U";

  return (
    <>
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0e4a7a] via-[#1d6cb1] to-[#2a83ce] text-white shadow-lg">
        {/* Top bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-3 group flex-shrink-0">
            <div className="w-14 h-14 rounded-xl bg-white/95 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform p-1">
              <img
                src="https://customer-assets.emergentagent.com/job_numero-builder/artifacts/yhm96yb7_VANDIERE.png"
                alt="Números Fácil"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-extrabold text-lg sm:text-xl">Números Fácil</span>
              <span className="text-[11px] sm:text-xs text-blue-100/90">Lotería Dominicana</span>
            </div>
          </a>

          <form onSubmit={onSubmit} className="hidden md:flex items-center bg-white/15 backdrop-blur rounded-full px-4 py-2 flex-1 max-w-sm border border-white/30 focus-within:bg-white/25 transition">
            <Search className="w-4 h-4 text-white/80 mr-2" />
            <input
              type="text"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="Buscar lotería..."
              className="bg-transparent outline-none text-sm w-full placeholder:text-white/70"
            />
          </form>

          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/tauro"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-bold text-sm shadow hover:shadow-lg hover:scale-[1.03] transition"
            >
              <Bot className="w-4 h-4" /> Tauro · Genera tus recomendaciones para ganar
            </Link>
            {isAuthenticated ? (
              <button
                onClick={() => setProfileOpen(true)}
                className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 transition"
              >
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-300 to-orange-500 text-[#0e4a7a] font-extrabold flex items-center justify-center text-sm">
                  {initials}
                </span>
                <span className="text-sm font-semibold max-w-[120px] truncate">{user.name}</span>
              </button>
            ) : (
              <>
                <button
                  onClick={openLogin}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold hover:bg-white/15 transition"
                >
                  <LogIn className="w-4 h-4" /> Entrar
                </button>
                <button
                  onClick={openRegister}
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-[#0e4a7a] font-bold text-sm shadow hover:shadow-lg hover:scale-[1.02] transition"
                >
                  <UserCircle2 className="w-4 h-4" /> Registrarme
                </button>
              </>
            )}
          </div>

          <button
            className="md:hidden p-2 rounded-lg bg-white/15 hover:bg-white/25"
            onClick={() => setOpenMobile((v) => !v)}
            aria-label="Menú"
          >
            {openMobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="hidden md:block border-t border-white/15">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-1 flex-wrap">
            {navigation.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <a
                  href={item.href}
                  className="px-4 py-2 rounded-full text-sm font-medium hover:bg-white/15 inline-flex items-center gap-1 transition-colors"
                >
                  {item.label}
                  {item.children && <ChevronDown className="w-4 h-4" />}
                </a>
                {item.children && openDropdown === item.label && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-56 bg-white rounded-xl shadow-2xl border border-blue-100 overflow-hidden text-gray-800 z-50">
                    {item.children.map((c) => (
                      <a
                        key={c.label}
                        href={c.href}
                        className="block px-4 py-2.5 text-sm hover:bg-blue-50 hover:text-[#0e4a7a] transition-colors"
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        {openMobile && (
          <div className="md:hidden bg-[#0e4a7a] border-t border-white/15">
            <div className="px-4 py-3 flex items-center bg-white/10 mx-4 my-3 rounded-full">
              <Search className="w-4 h-4 text-white/80 mr-2" />
              <input
                type="text"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { submit(local); setOpenMobile(false); } }}
                placeholder="Buscar lotería..."
                className="bg-transparent outline-none text-sm w-full placeholder:text-white/70"
              />
            </div>
            <div className="px-4 pb-3 flex gap-2">
              <Link
                to="/tauro"
                onClick={() => setOpenMobile(false)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-red-500 text-white font-bold text-sm"
              >
                <Bot className="w-4 h-4" /> Tauro · Genera tus recomendaciones para ganar
              </Link>
            </div>
            <div className="px-4 pb-3 flex gap-2">
                {isAuthenticated ? (
                <button
                  onClick={() => { setProfileOpen(true); setOpenMobile(false); }}
                  className="flex-1 px-4 py-2 rounded-full bg-white/15 font-semibold text-sm"
                >
                  Mi cuenta
                </button>
              ) : (
                <>
                  <button onClick={() => { openLogin(); setOpenMobile(false); }} className="flex-1 px-4 py-2 rounded-full bg-white/15 font-semibold text-sm">Entrar</button>
                  <button onClick={() => { openRegister(); setOpenMobile(false); }} className="flex-1 px-4 py-2 rounded-full bg-yellow-400 text-[#0e4a7a] font-bold text-sm">Registrarme</button>
                </>
              )}
            </div>
            <ul className="px-2 pb-4">
              {navigation.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="block px-4 py-3 text-sm font-medium hover:bg-white/10 rounded-lg"
                  >
                    {item.label}
                  </a>
                  {item.children && (
                    <ul className="pl-6">
                      {item.children.map((c) => (
                        <li key={c.label}>
                          <a href={c.href} className="block px-4 py-2 text-xs text-blue-100 hover:text-white">
                            {c.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultTab={authTab} />
      <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
